import OT from "@opentok/client";

import {
  CAM_MODE,
  type resolution,
  type VideoCallDeviceConfig,
  type VideoDimensions,
} from "../../definitions";

import useVideoCallStore from "@/Modules/VideoCall/Store";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { danger, log } from "@/Utils/Debug";
import { waitFor, defer } from "@/Utils/Promises";

import { DEFAULT_RESOLUTION, DEFAULT_RESOLUTION_SHARE_SCREEN } from "../../constants";

interface PublisherCallbacks {
  onVideoReady?: (video: HTMLVideoElement | HTMLObjectElement) => void;
  onDimensionsChanged?: (dimensions: VideoDimensions) => void;
}

interface PublisherConfig {
  cameraMode?: CAM_MODE;
  isShareScreen?: boolean;
  resolution?: resolution;
}

const ERROR_INVALID_CONFIG = "OT_CONSTRAINTS_NOT_SATISFIED";

export const getPublisher = (
  config: PublisherConfig,
  callbacks?: PublisherCallbacks
): Promise<OT.Publisher | undefined> => {
  const videoCallStore = useVideoCallStore();
  const configProps: (keyof OT.PublisherProperties)[] = [
    "frameRate",
    "facingMode",
    "resolution",
    "audioFallback",
    "videoSource",
    "audioSource",
  ];
  const shareScreenConfig: OT.PublisherProperties = {
    videoSource: "screen",
    scalableScreenshare: true,
    maxResolution: { height: 1080, width: 1920 },
    resolution: isValidString(config.resolution) ? config.resolution : DEFAULT_RESOLUTION_SHARE_SCREEN,
  };
  const camConfig: OT.PublisherProperties = {
    resolution: isValidString(config.resolution) ? config.resolution : DEFAULT_RESOLUTION,
    mirror: false,
  };

  let pConfig: OT.PublisherProperties = {
    insertMode: "replace",
    width: "100%",
    height: "100%",
    fitMode: "contain",
    frameRate: 15,
    scalableVideo: true, // only for routed sessions
    insertDefaultUI: false,
    showControls: false,
  };

  if (hasValue(config.cameraMode)) {
    camConfig.facingMode = config.cameraMode;
  }

  if (config.isShareScreen === true) {
    pConfig = { ...pConfig, ...shareScreenConfig };
  } else {
    pConfig = {
      ...pConfig,
      ...camConfig,
      //autoGainControl: true,
      audioFallback: { publisher: true },
    };
    const streamsConfig = videoCallStore.getConfig();

    if (hasValue(streamsConfig)) {
      if (isValidString(streamsConfig.videoId)) {
        pConfig.videoSource = streamsConfig.videoId;
      }

      if (!hasValue(streamsConfig.videoId)) {
        pConfig.videoSource = false;
      }

      if (isValidString(streamsConfig.audioId)) {
        pConfig.audioSource = streamsConfig.audioId;
      }

      pConfig.mirror = streamsConfig.mirror;

      if (isValidString(streamsConfig.blur)) {
        pConfig.videoFilter = { type: "backgroundBlur", blurStrength: "high" };
      }
    }
  }

  log("Publisher config", pConfig);

  const deferPromise = defer<OT.Publisher, OT.OTError | undefined>();
  let publisher: OT.Publisher | undefined = undefined;
  const cents = { publisher: false, init: false };

  const addCallbacks = (p: OT.Publisher) => {
    if (hasValue(callbacks)) {
      if (hasValue(callbacks.onVideoReady)) {
        p.on("videoElementCreated", (event) => {
          if (hasValue(event.element)) {
            callbacks.onVideoReady!(event.element);
          }
        });
      }

      if (hasValue(callbacks.onDimensionsChanged)) {
        p.on("streamCreated", (event) => {
          if (hasValue(event.stream)) {
            callbacks.onDimensionsChanged!({ width: p.videoWidth()!, height: p.videoHeight()! });
          }
        });
      }
    }
  };

  const resolvePromise = (p: OT.Publisher | undefined) => {
    if (cents.publisher && cents.init) {
      if (hasValue(p)) {
        deferPromise.resolve(p);
      } else {
        deferPromise.reject(undefined);
      }
    }
  };

  const rejectPromise = (err: OT.OTError) => {
    deferPromise.reject(err);
  };

  const callbackErrorInitPublisher = (err?: OT.OTError) => {
    log("callbackInitPublisher", err);
    cents.init = true;

    if (hasValue(err)) {
      publisher = undefined;

      if (
        config.isShareScreen !== true &&
        isValidString(err.name) &&
        err.name.toUpperCase() === ERROR_INVALID_CONFIG.toUpperCase()
      ) {
        if (Object.keys(pConfig).find((key) => configProps.includes(key as keyof OT.PublisherProperties))) {
          log("Publisher config not satisfied, reset it", pConfig);

          configProps.forEach((key) => {
            if (hasValue(pConfig[key])) {
              log(`Clean [${key}] config`, pConfig[key]);

              delete pConfig[key];
            }
          });

          cents.init = false;
          publisher = OT.initPublisher(undefined, pConfig, callbackErrorInitPublisher);

          addCallbacks(publisher);

          cents.publisher = true;

          resolvePromise(publisher);
        } else {
          rejectPromise(err);
        }
      } else {
        rejectPromise(err);
      }
    } else {
      resolvePromise(publisher);
    }
  };

  publisher = OT.initPublisher(undefined, pConfig, callbackErrorInitPublisher);

  addCallbacks(publisher);

  cents.publisher = true;

  resolvePromise(publisher);

  return deferPromise.promise;
};

const getCameraDevices = (): Promise<OT.Device[]> => {
  return new Promise((resolve) => {
    OT.getDevices((err, devices) => {
      if (hasValue(err)) {
        danger("Error getting devices", err);
        resolve([]);
      } else if (!hasValue(devices)) {
        resolve([]);
      } else {
        resolve(devices!.filter((d) => d.kind === "videoInput"));
      }
    });
  });
};

export const getVideoDevicesConfig = async (): Promise<VideoCallDeviceConfig[]> => {
  const devices = await getCameraDevices();

  const getDeviceIdFromStream = (stream: MediaStream): string | undefined => {
    const videoTracks = stream.getVideoTracks();

    if (!hasValue(videoTracks) || videoTracks.length === 0) {
      stream.getTracks().forEach((track) => track.stop());

      return undefined;
    }

    const deviceId = videoTracks[0].getSettings().deviceId;

    stream.getTracks().forEach((track) => track.stop());

    if (!isValidString(deviceId) || !hasValue(devices.find((d) => d.deviceId === deviceId))) {
      return undefined;
    }

    return deviceId;
  };

  const envDeviceId = await navigator.mediaDevices
    .getUserMedia({ video: { facingMode: CAM_MODE.ENVIRONMENT, width: 1, height: 1 } })
    .then(getDeviceIdFromStream);

  await waitFor(0.2);

  navigator.mediaDevices.dispatchEvent(new Event("devicechange"));

  return [{ type: CAM_MODE.ENVIRONMENT, deviceId: envDeviceId }];
};
